import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const statusBadge = (status) => {
  const styles = {
    new: 'bg-blue-50 text-blue-600 border-blue-100',
    confirmed: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    pending: 'bg-amber-50 text-amber-600 border-amber-100',
    declined: 'bg-red-50 text-red-600 border-red-100',
  };
  const dotStyles = {
    new: 'bg-blue-500',
    confirmed: 'bg-emerald-500',
    pending: 'bg-amber-500',
    declined: 'bg-red-500',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[status] || dotStyles.pending}`} />
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
    </span>
  );
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const initialsColors = [
  'bg-primary/10 text-primary',
  'bg-orange-100 text-orange-600',
  'bg-blue-100 text-blue-600',
  'bg-purple-100 text-purple-600',
  'bg-emerald-100 text-emerald-600',
];

const ToggleSwitch = ({ checked, onChange, disabled, size = 'md' }) => {
  const sizeClasses = size === 'sm'
    ? 'w-9 h-5 after:h-4 after:w-4'
    : 'w-11 h-6 after:h-5 after:w-5';
  return (
    <label className={`inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div className={`relative ${sizeClasses} bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:transition-all peer-checked:bg-primary`} />
    </label>
  );
};

const GuestTable = ({ guests, onToggleField, onEdit, onDelete, onSendWA, onViewReceipt }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = (guest) => {
    if (deleteConfirm === guest.id) {
      onDelete(guest.id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(guest.id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  if (!guests || guests.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        <Icon name="Users" size={48} className="mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-muted-foreground">No guests found. Add your first guest or wait for RSVP submissions.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-romantic-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-border bg-primary/5">
              <th className="py-3 pl-5 pr-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Name / Contact</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Category</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider text-center">Pax</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider text-center">Gift</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider text-center">Attended</th>
              <th className="py-3 px-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider text-center">Auto-Send</th>
              <th className="py-3 pl-3 pr-5 font-semibold text-xs text-muted-foreground uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {guests.map((guest, idx) => (
              <tr key={guest.id} className="group hover:bg-primary/5 transition-colors">
                <td className="py-3 pl-5 pr-3">
                  <div className="flex items-center gap-3">
                    <div className={`${initialsColors[idx % initialsColors.length]} rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm flex-shrink-0`}>
                      {getInitials(guest.guest_name)}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-foreground truncate">{guest.guest_name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {guest.phone || guest.email || 'No contact'}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className="text-sm text-muted-foreground">{guest.category || 'Guest'}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-sm font-medium text-foreground">{guest.number_of_guests || 1}</span>
                </td>
                <td className="py-3 px-3">
                  {statusBadge(guest.status)}
                </td>
                <td className="py-3 px-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => onToggleField(guest.id, 'gift_received', !guest.gift_received)}
                      className={`transition-colors ${guest.gift_received ? 'text-primary' : 'text-gray-300 hover:text-primary/50'}`}
                      title={guest.gift_received ? 'Gift received' : 'No gift yet'}
                    >
                      <Icon name="Gift" size={20} />
                    </button>
                    {guest.gift_receipt_url && (
                      <button
                        onClick={() => onViewReceipt && onViewReceipt(guest)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="View receipt"
                      >
                        <Icon name="Receipt" size={16} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-3 px-3 text-center">
                  <ToggleSwitch
                    checked={!!guest.attended}
                    onChange={() => onToggleField(guest.id, 'attended', !guest.attended)}
                    disabled={guest.status === 'declined'}
                  />
                </td>
                <td className="py-3 px-3 text-center">
                  <ToggleSwitch
                    checked={!!guest.auto_send_wa}
                    onChange={() => onToggleField(guest.id, 'auto_send_wa', !guest.auto_send_wa)}
                    disabled={guest.status === 'declined'}
                    size="sm"
                  />
                </td>
                <td className="py-3 pl-3 pr-5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {guest.phone && (
                      <button
                        onClick={() => onSendWA(guest)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-all"
                        title="Send WhatsApp"
                      >
                        <Icon name="MessageCircle" size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(guest)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit"
                    >
                      <Icon name="Pencil" size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(guest)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-all ${
                        deleteConfirm === guest.id
                          ? 'bg-red-500 text-white'
                          : 'bg-muted text-muted-foreground hover:text-red-500'
                      }`}
                      title={deleteConfirm === guest.id ? 'Click again to confirm' : 'Delete'}
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GuestTable;
